// Common App colleges grid (colleges_<cycle>.json, built from the official
// ReqGrid PDF by server/fillapp/scripts/build_colleges.py) + display helpers.

export interface CollegeDeadlines {
    ed: string | null;    // ISO date, 'rolling', or null
    edii: string | null;
    ea: string | null;
    eaii: string | null;
    rea: string | null;
    rd: string | null;
}

export interface CollegeRecs {
    te: number;   // teacher evaluations required
    oe: number;   // other evaluations required
    mr: boolean;  // midyear report
    cr: boolean;  // counselor recommendation
}

export interface College {
    id: string;
    name: string;
    schoolType: 'Coed' | 'Women' | 'Men' | 'Coordinate';
    deadlines: CollegeDeadlines;
    fees: { us: number | null; intl: number | null };
    personalEssay: boolean;
    coursesGrades: boolean;
    portfolio: string | null;        // 'SR' (SlideRoom) | 'COL' (college's own)
    writingSupplement: boolean;
    testPolicy: string | null;       // A | F | I | N | S
    testsUsed: string | null;
    intlEnglish: string | null;
    recs: CollegeRecs;
    savesForms: boolean;
    feeWaiver?: 'accepted' | 'not_accepted' | 'us_only' | null;
    // Common App's own member id. `id` is its string form — stable across
    // renames, unlike the name-slug ids used before 2026-27.
    memberId?: number;
    // The old name-slug id, kept so stored choices can be migrated.
    slug?: string;
    testPolicyUrl?: string | null;
}

export interface CollegesGrid {
    cycleYear: number;
    updatedAt: string | null;
    source: string;
    colleges: College[];
}

// Student's saved selection (StudentFillApp.colleges).
export interface CollegeChoice {
    id: string;
    addedAt: string;
    // Chosen application plan ('ed'|'edii'|'ea'|'eaii'|'rea'|'rd'); absent = undecided.
    plan?: string | null;
}

export const TEST_POLICY_LABELS: Record<string, string> = {
    A: 'Tests always required',
    F: 'Test flexible',
    I: 'Tests ignored',
    N: 'Tests never required',
    S: 'Tests sometimes required',
};

export const DEADLINE_LABELS: Array<{ key: keyof CollegeDeadlines; label: string }> = [
    { key: 'ed', label: 'ED' },
    { key: 'edii', label: 'ED II' },
    { key: 'ea', label: 'EA' },
    { key: 'eaii', label: 'EA II' },
    { key: 'rea', label: 'REA' },
    { key: 'rd', label: 'RD' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDeadline(v: string | null): string {
    if (!v) return '';
    if (v === 'rolling') return 'Rolling';
    const [y, m, d] = v.split('-').map(Number);
    if (!y || !m || !d) return v;
    return `${MONTHS[m - 1]} ${d}, ${y}`;
}

export function deadlineSummary(d: CollegeDeadlines): Array<{ key: keyof CollegeDeadlines; label: string; value: string }> {
    return DEADLINE_LABELS
        .filter(({ key }) => d[key])
        .map(({ key, label }) => ({ key, label, value: formatDeadline(d[key]) }));
}

export function recsSummary(r: CollegeRecs): string {
    const parts: string[] = [];
    if (r.te) parts.push(`${r.te} teacher eval${r.te > 1 ? 's' : ''}`);
    if (r.oe) parts.push(`${r.oe} other eval${r.oe > 1 ? 's' : ''}`);
    if (r.cr) parts.push('counselor rec');
    if (r.mr) parts.push('midyear report');
    return parts.join(' · ');
}

export function feeLabel(fees: { us: number | null; intl: number | null }): string {
    if (fees.us == null) return '';
    if (fees.us === 0) return 'No fee';
    return `$${fees.us}`;
}

// ---- Combined requirements (roll-up across the student's list) -------------

export interface CombinedRequirements {
    count: number;
    fees: { total: number; freeCount: number };
    // Every dated deadline across every college, grouped by date, chronological.
    deadlineGroups: Array<{ date: string; items: Array<{ college: string; label: string }> }>;
    rolling: string[];        // colleges with rolling admission
    noDeadlines: string[];    // colleges whose grid row lists no deadlines at all
    personalEssay: string[];
    writingSupplement: Array<{ id: string; name: string }>;
    coursesGrades: string[];
    portfolios: Array<{ id: string; college: string; system: string }>;
    testsRequired: string[];                                  // policy A
    testsConditional: Array<{ college: string; policy: string }>;  // F or S
    teacherEvals: { max: number; colleges: string[] };
    otherEvals: { max: number; colleges: string[] };
    counselorRec: string[];
    midyearReport: string[];
    // Earliest effective deadline per requirement (chosen plan's date, else the
    // college's earliest dated deadline); null = no dated deadline applies.
    dueBy: {
        personalEssay: string | null;
        satAct: string | null;
        coursesGrades: string | null;
        teacherEvals: string | null;
        otherEvals: string | null;
        counselorRec: string | null;
        midyearReport: string | null;
        fees: string | null;
        supplements: Record<string, string | null>;
        portfolios: Record<string, string | null>;
    };
}

// plans: chosen application plan per college id ('ed'|'edii'|…). A college
// with no stored plan DEFAULTS TO RD (when its grid row offers one) — every
// college always has exactly one active plan, and the checklist due-by dates
// follow it. Colleges with no RD/Rolling at all fall back to their earliest
// dated deadline.
export function combineRequirements(
    colleges: College[],
    plans?: Record<string, string | null | undefined>,
): CombinedRequirements {
    const byDate = new Map<string, Array<{ college: string; label: string }>>();
    const rolling: string[] = [];
    const noDeadlines: string[] = [];
    const out: CombinedRequirements = {
        count: colleges.length,
        fees: { total: 0, freeCount: 0 },
        deadlineGroups: [],
        rolling,
        noDeadlines,
        personalEssay: [],
        writingSupplement: [],
        coursesGrades: [],
        portfolios: [],
        testsRequired: [],
        testsConditional: [],
        teacherEvals: { max: 0, colleges: [] },
        otherEvals: { max: 0, colleges: [] },
        counselorRec: [],
        midyearReport: [],
        dueBy: {
            personalEssay: null, satAct: null, coursesGrades: null,
            teacherEvals: null, otherEvals: null, counselorRec: null,
            midyearReport: null, fees: null, supplements: {}, portfolios: {},
        },
    };
    const bump = (current: string | null, d: string | null): string | null => {
        if (!d) return current;
        return current === null || d < current ? d : current;
    };
    for (const c of colleges) {
        // Implicit default: Regular Decision (covers 'rolling' too, which the
        // grid stores in the rd slot).
        const chosen = plans?.[c.id] || (c.deadlines.rd ? 'rd' : null);
        const chosenValue = chosen ? c.deadlines[chosen as keyof CollegeDeadlines] : null;
        let dated = false;
        let rolls = false;
        let earliest: string | null = null;
        for (const { key, label } of DEADLINE_LABELS) {
            const v = c.deadlines[key];
            if (!v) continue;
            if (v === 'rolling') {
                if (!chosenValue || chosen === key) rolls = true;
                continue;
            }
            earliest = bump(earliest, v);
            // A chosen plan narrows the Deadlines section to just that date.
            if (chosenValue && chosen !== key) continue;
            dated = true;
            const items = byDate.get(v) || [];
            items.push({ college: c.name, label });
            byDate.set(v, items);
        }
        if (rolls && !dated) rolling.push(c.name);
        if (!dated && !rolls) noDeadlines.push(c.name);
        // The date this college's materials are effectively due.
        const effective = chosenValue && chosenValue !== 'rolling'
            ? chosenValue
            : (chosenValue === 'rolling' ? null : earliest);

        if (c.fees.us != null) {
            out.fees.total += c.fees.us;
            if (c.fees.us === 0) out.fees.freeCount += 1;
        }
        out.dueBy.fees = bump(out.dueBy.fees, effective);
        if (c.personalEssay) {
            out.personalEssay.push(c.name);
            out.dueBy.personalEssay = bump(out.dueBy.personalEssay, effective);
        }
        if (c.writingSupplement) {
            out.writingSupplement.push({ id: c.id, name: c.name });
            out.dueBy.supplements[c.id] = effective;
        }
        if (c.coursesGrades) {
            out.coursesGrades.push(c.name);
            out.dueBy.coursesGrades = bump(out.dueBy.coursesGrades, effective);
        }
        if (c.portfolio) {
            out.portfolios.push({
                id: c.id,
                college: c.name,
                system: c.portfolio === 'SR' ? 'SlideRoom' : 'college site',
            });
            out.dueBy.portfolios[c.id] = effective;
        }
        if (c.testPolicy === 'A') {
            out.testsRequired.push(c.name);
            out.dueBy.satAct = bump(out.dueBy.satAct, effective);
        } else if (c.testPolicy === 'F' || c.testPolicy === 'S') {
            out.testsConditional.push({ college: c.name, policy: c.testPolicy });
        }
        if (c.recs.te > out.teacherEvals.max) {
            out.teacherEvals = { max: c.recs.te, colleges: [c.name] };
        } else if (c.recs.te === out.teacherEvals.max && c.recs.te > 0) {
            out.teacherEvals.colleges.push(c.name);
        }
        if (c.recs.oe > out.otherEvals.max) {
            out.otherEvals = { max: c.recs.oe, colleges: [c.name] };
        } else if (c.recs.oe === out.otherEvals.max && c.recs.oe > 0) {
            out.otherEvals.colleges.push(c.name);
        }
        if (c.recs.te > 0) out.dueBy.teacherEvals = bump(out.dueBy.teacherEvals, effective);
        if (c.recs.oe > 0) out.dueBy.otherEvals = bump(out.dueBy.otherEvals, effective);
        if (c.recs.cr) {
            out.counselorRec.push(c.name);
            out.dueBy.counselorRec = bump(out.dueBy.counselorRec, effective);
        }
        if (c.recs.mr) {
            out.midyearReport.push(c.name);
            out.dueBy.midyearReport = bump(out.dueBy.midyearReport, effective);
        }
    }
    out.deadlineGroups = Array.from(byDate.entries())
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([date, items]) => ({ date, items }));
    return out;
}

// "A, B, C +2 more" — keeps summary rows readable with a long college list.
export function joinNames(names: string[], max: number = 4): string {
    if (names.length <= max) return names.join(', ');
    return `${names.slice(0, max).join(', ')} +${names.length - max} more`;
}

// ---- Student checklist ----------------------------------------------------

export interface ChecklistItem {
    key: string;      // stable slug the student's check is stored under
    label: string;
    detail?: string;
    dueBy?: string | null;   // earliest effective deadline (ISO) among requiring colleges
}

// The single source for what appears in the "What you'll need" checklist.
// Requirements nobody on the list has are omitted. Keys survive college-list
// churn by design; the teacher/other-eval keys encode the required count so a
// RAISED requirement (2 needed → 3 needed) auto-unchecks for re-confirmation.
export function buildChecklistItems(req: CombinedRequirements): ChecklistItem[] {
    const items: ChecklistItem[] = [];
    if (req.personalEssay.length) {
        items.push({
            key: 'personalEssay',
            label: 'Personal essay',
            detail: `required by ${req.personalEssay.length} of ${req.count}`,
            dueBy: req.dueBy.personalEssay,
        });
    }
    for (const s of req.writingSupplement) {
        items.push({ key: `supp:${s.id}`, label: 'Writing supplement', detail: s.name, dueBy: req.dueBy.supplements[s.id] });
    }
    const testingParts: string[] = [];
    if (req.testsRequired.length) testingParts.push(`required by ${req.testsRequired.length}`);
    if (req.testsConditional.length) testingParts.push(`flexible or sometimes at ${req.testsConditional.length}`);
    if (testingParts.length) {
        items.push({ key: 'satAct', label: 'SAT/ACT scores', detail: testingParts.join(' · '), dueBy: req.dueBy.satAct });
    }
    if (req.coursesGrades.length) {
        items.push({
            key: 'coursesGrades',
            label: 'Courses & grades self-report',
            detail: `required by ${req.coursesGrades.length} of ${req.count}`,
            dueBy: req.dueBy.coursesGrades,
        });
    }
    for (const p of req.portfolios) {
        items.push({ key: `portfolio:${p.id}`, label: 'Portfolio', detail: `${p.college} (${p.system})`, dueBy: req.dueBy.portfolios[p.id] });
    }
    if (req.teacherEvals.max) {
        items.push({
            key: `teacherEvals:${req.teacherEvals.max}`,
            label: 'Teacher evaluations',
            detail: `${req.teacherEvals.max} needed`,
            dueBy: req.dueBy.teacherEvals,
        });
    }
    if (req.otherEvals.max) {
        items.push({
            key: `otherEvals:${req.otherEvals.max}`,
            label: 'Other evaluations',
            detail: `${req.otherEvals.max} needed`,
            dueBy: req.dueBy.otherEvals,
        });
    }
    if (req.counselorRec.length) {
        items.push({
            key: 'counselorRec',
            label: 'Counselor recommendation',
            detail: `required by ${req.counselorRec.length} of ${req.count}`,
            dueBy: req.dueBy.counselorRec,
        });
    }
    if (req.midyearReport.length) {
        items.push({
            key: 'midyearReport',
            label: 'Midyear report',
            detail: `required by ${req.midyearReport.length} of ${req.count}`,
            dueBy: req.dueBy.midyearReport,
        });
    }
    items.push({
        key: 'fees',
        label: 'Application fees',
        detail: `$${req.fees.total} total${req.fees.freeCount ? ` · ${req.fees.freeCount} free` : ''}`,
        dueBy: req.dueBy.fees,
    });
    return items;
}
